import React, { useEffect, useState } from 'react';
import { toJS } from 'mobx';
import useStore from '@hooks/useStore';
import { useTranslation } from 'react-i18next';

import { HeadCell } from './EnhancedTableHead';
import { AccountsPayableLinkStatus } from '@constants/accountsPayableLink';

interface IUseAccountsPayableCustomTableProps {
  grouped: boolean;
  groupedRowsIds: string[];
  groupedTotalRows: number;
  rows: any[];
  autoRefreshTotals: boolean;
}

const beforeDispersionStatus = [
  AccountsPayableLinkStatus.ACTIVE,
  AccountsPayableLinkStatus.CONFIRMED,
  AccountsPayableLinkStatus.CANCELED,
  AccountsPayableLinkStatus.FUNDED,
];

function useAccountsPayableCustomTable({
  grouped,
  groupedRowsIds,
  rows,
  autoRefreshTotals,
  groupedTotalRows,
}: IUseAccountsPayableCustomTableProps): any {
  const { t } = useTranslation();
  const { accountsPayableLink } = useStore();
  const { accountsPayable, tools, status } = accountsPayableLink;
  const {
    canProcess,
    accountsPayableIds,
    getSelectedIds,
    getSelectedIdsLength,
    setSelectedIds,
    setUnselectedIds,
    handleGlobalTotalAmount,
    unselectedIds,
  } = accountsPayable;
  const { toolsConfigShowColumns, toolsSelectedColumns, getToolsSelectedColumnsLength } = tools;

  const [headCells, setHeadCells] = useState<HeadCell[]>([]);
  const [totalVisibleColumns, setTotalVisibleColumns] = useState(5);
  const [totalAmount, setTotalAmount] = useState<any>(null);

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelecteds = canProcess.map((n) => n.accountPayableId);
      setSelectedIds(newSelecteds);

      const unselecteds = accountsPayableIds.filter((item) => newSelecteds.indexOf(item) == -1);
      setUnselectedIds(unselecteds);
      handleTotalAmount(unselecteds);
      handleGlobalTotalAmount();
      return;
    }

    setUnselectedIds(toJS(accountsPayableIds));
    handleTotalAmount(accountsPayableIds);
    handleGlobalTotalAmount();
    setSelectedIds([]);
  };

  const handleClick = (event: React.MouseEvent<unknown>, name: string) => {
    const selectedIndex = getSelectedIds.indexOf(name);
    let newSelected: string[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(getSelectedIds, name);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(getSelectedIds.slice(1));
    } else if (selectedIndex === getSelectedIds.length - 1) {
      newSelected = newSelected.concat(getSelectedIds.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        getSelectedIds.slice(0, selectedIndex),
        getSelectedIds.slice(selectedIndex + 1),
      );
    }
    setSelectedIds(newSelected);

    const unselecteds = accountsPayableIds.filter((item) => newSelected.indexOf(item) == -1);

    setUnselectedIds(unselecteds);
    handleTotalAmount(unselecteds);
    handleGlobalTotalAmount();
  };

  const isSelected = (name: string) => getSelectedIds.indexOf(name) !== -1;

  const handleGetSelectedIdsLength = (): number => {
    if (grouped) {
      const globalSelectedIds = toJS(getSelectedIds);
      let total = 0;
      for (const id of groupedRowsIds) {
        if (globalSelectedIds.includes(id)) {
          total++;
        }
      }
      return total;
    }

    return getSelectedIdsLength;
  };

  const handleTotalAmount = (unselectedsIds: string[]) => {
    let accountsPayable = rows.map((item: any) => !item.error && item).filter(Boolean) as string[];

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    if (!beforeDispersionStatus.includes(status)) {
      accountsPayable = rows.map((item) => item);
    }

    let totalAmount = 0;
    for (const ap of accountsPayable) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const amount = ap.amount as any;
      const itemAmount = Number(amount);
      totalAmount = totalAmount + itemAmount;
    }

    let totalUnselectedAmount = 0;
    for (const id of unselectedsIds) {
      const ap = accountsPayable
        .map((item: any) => item.accountPayableId === id && Number(item.amount))
        .filter(Boolean) as number[];
      if (Object.keys(ap).length > 0) {
        totalUnselectedAmount = totalUnselectedAmount + ap[0];
      }
    }

    setTotalAmount(totalAmount - totalUnselectedAmount);
    return totalAmount - totalUnselectedAmount;
  };

  useEffect(() => {
    const headCells: HeadCell[] = [
      { id: 'provider', align: 'left', disablePadding: false, label: 'Proveedor', visible: true },
      { id: 'refNumber', align: 'left', disablePadding: false, label: 'Folio', visible: true },
      { id: 'status', align: 'left', disablePadding: false, label: 'Estatus', visible: true },
    ];
    let totalVisibles = 0;
    let defaultVisible = 5;
    if (!beforeDispersionStatus.includes(status)) {
      defaultVisible = 6;
      headCells.push({
        id: 'cepUrl',
        align: 'left',
        disablePadding: false,
        label: 'CEP',
        visible: true,
      });
    }
    for (const item of toolsConfigShowColumns) {
      const visible = toolsSelectedColumns.filter((v) => v.name === item.name);
      const isVisible = Object.keys(visible).length > 0;
      if (isVisible) {
        totalVisibles = totalVisibles + 1;
      }
      headCells.push({
        id: item.name,
        align: 'left',
        disablePadding: false,
        label: t(`accountsPayable.columnsName.${item.name}`),
        visible: isVisible,
      });
    }

    headCells.push({
      id: 'amount',
      align: 'right',
      disablePadding: false,
      label: 'Monto',
      visible: true,
    });

    setHeadCells(headCells);

    setTotalVisibleColumns(defaultVisible + totalVisibles);
  }, [toolsConfigShowColumns, getToolsSelectedColumnsLength, toolsSelectedColumns]);

  // Only first page load
  useEffect(() => {
    if (totalAmount === null) {
      setTotalAmount(0);
      handleTotalAmount(unselectedIds);
      handleGlobalTotalAmount();
    }
  }, [totalAmount]);

  // When select group by changes on validation step
  useEffect(() => {
    if (!autoRefreshTotals && (groupedTotalRows || groupedTotalRows === 0)) {
      setTotalAmount(0);
      handleTotalAmount(unselectedIds);
      handleGlobalTotalAmount();
    }
  }, [autoRefreshTotals, groupedTotalRows]);

  // Control auto refresh trhough firebase on resume & on dispersion steps
  useEffect(() => {
    if (autoRefreshTotals && (groupedTotalRows || groupedTotalRows === 0)) {
      setTotalAmount(0);
      handleTotalAmount([]);
      handleGlobalTotalAmount();
    }
  }, [autoRefreshTotals, groupedTotalRows]);

  return {
    headCells,
    totalVisibleColumns,
    handleSelectAllClick,
    handleClick,
    isSelected,
    handleGetSelectedIdsLength,
    totalAmount,
    getToolsSelectedColumnsLength,
  };
}

export default useAccountsPayableCustomTable;
